import {
  ContentFulScreens,
  ICharacter,
  NotificationType,
  Screen,
  useApp,
  useCms,
  useTranslation,
  useUserInfo,
  useUserInteractions,
  useUserSettings,
} from "@next-platform/client-common-core";
import { ListEmpty } from "@next-platform/client-common-spicy-ui";
import { useEffect, useMemo, useRef } from "react";
import { useHits, useInstantSearch } from "react-instantsearch";

import { CmsBannerService } from "@/components";
import { getOfflineNotificationMessage } from "@/components/common/Notification/utils/notification";
import ErrorSection from "@/components/ErrorSection/ErrorSection";
import { DisplayContext } from "@/components/search/types";
import { useCharacterActions, useCharacterData, useCharacterRecommendations } from "@/components/search/v2/hooks";
import { HIDE_RECOMMENDATION_DELAY_MS, ROUTES } from "@/constants";
import { useCenterElementDetection } from "@/hooks";
import useCharacterCardGridConfig from "@/hooks/useCharacterCardGridConfig";
import useLocalizedLocation from "@/hooks/useLocalizedLocation";
import useToast from "@/hooks/useToast";
import useAuth from "@/services/useAuth";
import { getBusinessRouteScreen, getCharacterHits, getNumberOfCols } from "@/utils";

import { SharedChatbotCard } from "../CharacterCards";
import CharacterGrid from "./CharacterGrid";
import CharacterModals from "./CharacterModals";

interface ISharedChatbotsHitsProps {
  context: DisplayContext;
  nsfwToggle: boolean;
  isPartner: boolean;
  currentCategory: string;
  recommId: string;
  shouldFilterNsfwCharacters: boolean;
}

const SharedChatbotsHits = ({ context, nsfwToggle, isPartner, currentCategory, recommId, shouldFilterNsfwCharacters }: ISharedChatbotsHitsProps) => {
  const characterCardContainerRef = useRef<HTMLDivElement>(null);
  const { t } = useTranslation();
  const { displayToast } = useToast();
  const { pagePath, search } = useLocalizedLocation();
  const { isAuthenticated, isLoading: isAuthLoading } = useAuth();
  const { userId } = useUserInfo();
  const { applicationName } = useApp();
  const { getInlineBannersRowMapping } = useCms();

  const {
    likedBots,
    blurNSFW,
    chatLanguage,
    isLoading: isSettingsLoading,
    areUserSettingsLoaded,
    onUserLikebot,
    lastTimeFilteredCharacters,
  } = useUserSettings();
  const { isSearchInProgress, sortBy } = useUserInteractions();

  const { items: hits, results } = useHits();
  const { status, error } = useInstantSearch({ catchError: true });

  const isHitsLoading = status === "loading" || status === "stalled";
  const screenName = getBusinessRouteScreen(pagePath) as Screen;
  const currentUrlOrigin = window.location.origin;

  const selectedTags = results?._state?.facetsRefinements?.tags;
  const searchedQuery = results?.query;
  const isTagsSelected = !!selectedTags?.length;
  const isSearchClientFirstPage = results?.page === 0;

  const isFilteredTimerExpired = !lastTimeFilteredCharacters || new Date().getTime() - lastTimeFilteredCharacters > HIDE_RECOMMENDATION_DELAY_MS;

  const isSortByDefault = useMemo(() => {
    const searchParams = new URLSearchParams(search);
    const sortByVal = searchParams.get("dev_characters[sortBy]") || searchParams.get("characters[sortBy]");
    return sortBy === null && sortByVal === null;
  }, [sortBy, search]);

  const shouldInjectRecommendations =
    userId &&
    (hits?.length ?? 0) > 0 &&
    (pagePath === "/" || pagePath === "/external") &&
    isFilteredTimerExpired &&
    isSearchClientFirstPage &&
    !isSearchInProgress &&
    isSortByDefault;

  const recommendations = useCharacterRecommendations({
    shouldInject: !!shouldInjectRecommendations,
    isAuthLoading: !isAuthenticated,
    userId: userId || "",
    nsfwToggle,
  });

  const charHits = getCharacterHits({
    hits: (hits || []) as unknown as ICharacter[],
    isAuthLoading,
    shouldInjectRecommendations: !!shouldInjectRecommendations,
    recommendations,
    recommId,
    searchedQuery,
    shouldFilterNsfwCharacters,
  });

  const {
    characterInfo,
    shareModalVisible,
    blockModalVisible,
    dislikedRecommendedCharacterIds,
    setShareModalVisible,
    setBlockModalVisible,
    characterCardActionsHandler,
  } = useCharacterActions({
    charHits,
    nsfwToggle,
    screenName,
    likedBots,
    onUserLikebot,
  });

  const memoizedData = useCharacterData({
    charHits: charHits || [],
    likedBots,
    chatLanguage,
    dislikedRecommendedCharacterIds,
    displayContext: context,
    isPartner,
    blurNSFW,
    isAuthenticated,
  });

  const { centeredElementIds, setElementRef } = useCenterElementDetection();

  const { colWidth, colGap } = useCharacterCardGridConfig();
  const containerWidth = characterCardContainerRef.current?.clientWidth || 0;
  const numberOfColumns = useMemo(() => getNumberOfCols({ containerWidth, colWidth, colGap }), [containerWidth, colWidth, colGap]);

  useEffect(() => {
    if (error && !navigator.onLine) {
      displayToast({ message: getOfflineNotificationMessage(t), type: NotificationType.warning });
    }
  }, [displayToast, error, t]);

  const emptyStates = {
    [ROUTES.favoriteBots]: {
      title: t("common:noResultsFound"),
      description: t("search:chatbots.favorites.noResults.description"),
    },
    default: {
      title: t("common:noResultsFound"),
      description: t("search:chatbots.noResults.description"),
    },
  };

  const emptyState = emptyStates[pagePath as keyof typeof emptyStates] || emptyStates.default;

  const isWaitingForSettings = isAuthenticated && !areUserSettingsLoaded;

  const isDataLoading =
    (isHitsLoading && charHits?.length === 0 && selectedTags?.length === 0 && (!searchedQuery || searchedQuery === "*")) || isWaitingForSettings;
  const noResult = !isHitsLoading && hits?.length === 0 && (isTagsSelected || searchedQuery);

  if (error && !isHitsLoading) {
    return <ErrorSection shouldCenter={true} buttonText={t("common:networkError.cta.retry")} />;
  }

  const rowMapping = getInlineBannersRowMapping(CmsBannerService.contentfulScreen as ContentFulScreens);
  const shouldShowLoading = isDataLoading || noResult;

  if (isAuthenticated && !areUserSettingsLoaded) {
    return (
      <div className="grow flex justify-center">
        <ListEmpty isLoading={true} title="" description="" />
      </div>
    );
  }

  return (
    <>
      <div ref={characterCardContainerRef}>
        {shouldShowLoading ? (
          <div className="grow flex justify-center">
            <ListEmpty isLoading={isDataLoading} title={emptyState.title} description={emptyState.description} />
          </div>
        ) : (
          <CharacterGrid numberOfColumns={numberOfColumns} inlineBannerMapping={rowMapping} isTagsSelected={isTagsSelected}>
            {memoizedData.map(
              (characterData) =>
                !characterData.isHidden && (
                  <SharedChatbotCard
                    key={characterData.characterId}
                    characterData={characterData}
                    onAction={(action, characterId) => {
                      void characterCardActionsHandler(action, characterId, currentCategory, userId);
                    }}
                    isAuthenticated={isAuthenticated}
                    centeredElementIds={centeredElementIds}
                    setElementRef={setElementRef}
                  />
                ),
            )}
          </CharacterGrid>
        )}
      </div>

      <CharacterModals
        characterInfo={characterInfo}
        shareModal={
          shareModalVisible && characterInfo
            ? {
                visible: shareModalVisible,
                setVisible: setShareModalVisible,
                characterId: characterInfo.character_id || characterInfo.id || "",
                currentUrlOrigin,
                applicationName: applicationName || "",
              }
            : undefined
        }
        blockModal={
          blockModalVisible
            ? {
                visible: blockModalVisible,
                setVisible: setBlockModalVisible,
                isLoading: isSettingsLoading,
              }
            : undefined
        }
      />
    </>
  );
};

export default SharedChatbotsHits;
